'use client';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { OrderInfoCards } from '@eliadmin/core/src/components/nt/account/orders/order-info';
import { OrderItemsCard } from '@eliadmin/core/src/components/nt/account/orders/order-items';
import { OrderStatusBadge } from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import { orderStage } from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import {
  countLabels,
  statusSteps,
  useOrderTexts,
} from '@eliadmin/core/src/components/nt/account/orders/order-texts';
import { OrderTimeline } from '@eliadmin/core/src/components/nt/account/orders/order-timeline';
import {
  orderCounts,
  orderDate,
  orderTime,
  trackingUrl,
} from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';

import { OrderLookupFields, OrderLookupNotice } from './order-lookup';
import { useOrderLookup } from './use-order-lookup';

const ID = 'order-status';

/**
 * The order the customer asked for, in the cards of the account's order detail (Figma 208:12024):
 * number with the status pill and „Vytvorená … · 3 produkty, 4 ks“, the status timeline, contact,
 * delivery, payment and billing address, the products with the totals (a guest has no order list,
 * so the lines are plain names). No invoice or „Kúpiť znova“ – both need the customer's token.
 */
function OrderStatusResult({ info }: { info: OrderInfo }) {
  const { t } = useTranslations();
  const { account, checkout } = useOrderTexts();
  const stage = orderStage(info.status);
  const counts = countLabels(checkout, orderCounts(info.items ?? []));
  const meta = counts
    ? fillTemplate(account.createdAt, {
        datum: orderDate(info.created_at),
        cas: orderTime(info.created_at),
        ...counts,
      })
    : '';

  return (
    <div className="flex flex-col gap-4 @container">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="nt-h3 min-w-0 text-[#0D110D] [overflow-wrap:anywhere]">
            {fillTemplate(account.order, { cislo: info.identifier }) ||
              `${t('text108')} ${info.identifier}`}
          </h2>
          <OrderStatusBadge status={info.status} stage={stage} />
        </div>
        {meta ? <p className="nt-body-m text-[#5D655D]">{meta}</p> : null}
      </header>
      <OrderTimeline
        info={info}
        steps={statusSteps(checkout)}
        tracking={stage === 'cancelled' ? '' : trackingUrl(info)}
        trackLabel={account.trackShipment}
      />
      <OrderLoyaltyReward reward={info.loyalty_reward} />
      <OrderInfoCards info={info} stage={stage} />
      <OrderItemsCard info={info} />
    </div>
  );
}

/** For a signed-in customer (core: „Ste prihlásený …“ + „Objednávky“): all orders live in the account. */
function SignedInNote({ text }: { text: string }) {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);
  if (!me || !text) return null;

  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-[#F6FBF7] p-5 sm:flex-row sm:items-center sm:px-6">
      <div className="flex min-w-0 flex-1 items-start gap-4 sm:items-center">
        <Icon name="user" size={32} className="shrink-0 text-[#127A26]" />
        <p className="nt-body-m text-[#363D36]">{text}</p>
      </div>
      <Button asChild variant="outline" className="w-full sm:w-auto">
        <Link href={ROUTES.ORDERS()} prefetch={false}>
          {t('text257')}
        </Link>
      </Button>
    </div>
  );
}

/**
 * `/stav-objednavky` (core `OrderStatusForm`, same call): the order number and the e-mail find the
 * order 500 ms after the last keystroke and its status appears below. `?identifier=…&email=…` (the
 * links of the shop's e-mails) fills the fields in and starts the lookup.
 */
export function OrderStatusView({ signedInText }: { signedInText: string }) {
  const { t } = useTranslations();
  const params = useSearchParams();
  const identifierParam = params.get('identifier') ?? '';
  const emailParam = params.get('email') ?? '';
  const [values, setValues] = useState({ identifier: identifierParam, email: emailParam });
  const { status, order } = useOrderLookup(values.identifier, values.email);

  // A link opened while the page is on screen starts a new lookup (client-side navigation).
  useEffect(() => {
    if (identifierParam || emailParam)
      setValues({ identifier: identifierParam, email: emailParam });
  }, [identifierParam, emailParam]);

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <section className="flex flex-col gap-4 rounded-2xl border border-[#E2E5DF] bg-white p-4 md:p-6">
        <OrderLookupFields
          prefix={ID}
          identifier={{
            value: values.identifier,
            onChange: event =>
              setValues(current => ({ ...current, identifier: event.target.value })),
          }}
          email={{
            value: values.email,
            onChange: event => setValues(current => ({ ...current, email: event.target.value })),
          }}
        />
        <OrderLookupNotice status={status} idleText={t('text2346')} />
      </section>
      {order ? <OrderStatusResult info={order} /> : null}
      <SignedInNote text={signedInText} />
    </div>
  );
}
